/**
 * Cross-subdomain "is a CWU member signed in?" flag.
 *
 * WHY THIS EXISTS
 * `learning.clubwealth.com` (this app) and `clubwealth.com` (the WordPress
 * marketing site) are different ORIGINS, so `localStorage` — where CWAuthToken
 * lives — is not shared between them. A cookie scoped to the shared parent
 * domain `.clubwealth.com` IS readable by both, and because they share a
 * registrable domain it is a FIRST-party cookie: unaffected by Safari ITP or
 * Chrome's third-party cookie phase-out.
 *
 * Used by gated pages on clubwealth.com (e.g. /aiquickstartdone/) to let an
 * already-signed-in member through without a token in the URL.
 *
 * 🔴 NEVER put CWAuthToken (or any real credential) in this cookie.
 * It is intentionally NOT httpOnly so plain JS on clubwealth.com can read it —
 * which means every script on that site can read it too, including WordPress
 * plugins and third-party marketing tags. Putting the real JWT here would turn
 * any WP plugin compromise into full CWU account takeover. The value is an
 * opaque flag and carries no identity.
 *
 * 🔴 NEVER rename this cookie to anything starting with `wordpress` or `wp-`.
 * Verified 2026-07-28: GoDaddy's edge returns `x-gateway-skip-cache: 1` +
 * `no-store` when a `wordpress_logged_in_*` cookie is present, which would
 * disable edge caching across ALL of clubwealth.com for every signed-in member.
 * A custom name (`cw_u`) leaves the cache untouched (`skip-cache: 0`, HIT).
 *
 * This is deterrence, not authorization — it stops a non-technical visitor from
 * reaching a gated page by URL or from sharing a working link. Anyone who opens
 * devtools can set the cookie themselves. Do not use it to protect anything
 * that actually needs to be secure.
 */

const COOKIE_NAME = 'cw_u';
const PARENT_DOMAIN = '.clubwealth.com';
const MAX_AGE_SECONDS = 12 * 60 * 60; // 12h — refreshed on every app load

/** True only on the real clubwealth.com estate; localhost/staging get no cookie. */
const onClubWealthDomain = (): boolean =>
  window.location.hostname.endsWith('clubwealth.com');

/** Mark this browser as holding a signed-in CWU session. Safe to call repeatedly. */
export const setCrossDomainMemberFlag = (): void => {
  if (!onClubWealthDomain()) return;
  document.cookie =
    `${COOKIE_NAME}=1; Domain=${PARENT_DOMAIN}; Path=/; ` +
    `Max-Age=${MAX_AGE_SECONDS}; Secure; SameSite=Lax`;
};

/** Clear the flag. Call anywhere CWAuthToken is removed. */
export const clearCrossDomainMemberFlag = (): void => {
  if (!onClubWealthDomain()) return;
  document.cookie =
    `${COOKIE_NAME}=; Domain=${PARENT_DOMAIN}; Path=/; Max-Age=0; ` +
    `Secure; SameSite=Lax`;
};
